/**
 * The history of one config section, out of [Reporting].[VatConfigAudit].
 *
 * WHY BEFORE AND AFTER AND NOT A DIFF LINE. A mapping row is eleven interdependent fields;
 * "Kennzahl: — → 42" on its own hides that LineRole and the tax rate moved with it. So the
 * changed fields are named and each shows both sides. An INSERT shows what was created, a
 * DELETE what disappeared - the deleted row's contents are the only place its rule still
 * exists, which is the point of keeping them.
 *
 * Collapsed by default. Somebody opening the settings tab is going there to change something,
 * not to read three years of history, and the audit read is a second request.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, History } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import { useVatLang } from './VatLang';
import type {
    ConfigAuditEntry, ConfigResource, ConfigResourceContract, ConfigRow, VatCompany,
} from '../../types/vatReporting';

interface Props {
    company: VatCompany;
    country: string;
    resource: ConfigResource;
    contract: ConfigResourceContract;
    /** Bumped by the parent after every write, so the panel reloads without being reopened. */
    reloadToken: number;
    onError: (message: string) => void;
}

const ACTION_STYLE: Record<string, string> = {
    INSERT: 'bg-green-100 text-green-800 border-green-200',
    UPDATE: 'bg-amber-100 text-amber-800 border-amber-200',
    DELETE: 'bg-red-100 text-red-800 border-red-200',
};

/** A value as one short readable token. Empty string is a real value and has to be visible. */
function show(v: unknown, emptyLabel: string, notSetLabel: string): string {
    if (v === null || v === undefined) return notSetLabel;
    if (v === '') return emptyLabel;
    if (typeof v === 'boolean') return v ? '✓' : '—';
    return String(v);
}

export function ConfigAuditPanel({
    company, country, resource, contract, reloadToken, onError,
}: Props) {
    const { t, tOr } = useVatLang();
    const [open, setOpen] = useState(false);
    const [entries, setEntries] = useState<ConfigAuditEntry[] | null>(null);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        vatReportingApi.getConfigAudit({ company, country, resource, limit: 50 })
            .then(r => { setEntries(r.entries); setTruncated(r.truncated); })
            .catch(err => onError(err instanceof Error ? err.message : String(err)))
            .finally(() => setLoading(false));
    }, [company, country, resource, onError]);

    useEffect(() => {
        if (!open) return;
        load();
    }, [open, load, reloadToken]);

    // A write while the panel is closed must not leave a stale list behind for the next open.
    useEffect(() => { setEntries(null); }, [company, country, resource]);

    const emptyLabel = t('config.emptyValue');
    const notSetLabel = t('config.notSet');

    const keyLine = (key: ConfigRow) => contract.fields
        .filter(f => f.key && f.name !== 'companyCode' && f.name !== 'countryCode')
        .map(f => show(key[f.name], emptyLabel, notSetLabel))
        .join(' / ');

    return (
        <div className="border rounded bg-white">
            <button onClick={() => setOpen(o => !o)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <History className="h-4 w-4 text-gray-400" />
                {t('config.historyTitle')}
                {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
            </button>

            {open && (
                <div className="border-t px-4 py-3">
                    {!loading && entries && entries.length === 0 && (
                        <p className="text-sm text-gray-500">{t('config.historyEmpty')}</p>
                    )}
                    {entries && entries.length > 0 && (
                        <>
                            <ul className="space-y-2">
                                {entries.map(e => (
                                    <li key={e.id} className="text-sm border-b last:border-b-0 pb-2 last:pb-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className={`px-1.5 py-0.5 rounded text-xs border ${ACTION_STYLE[e.action] ?? ''}`}>
                                                {tOr(`config.action.${e.action}`, e.action)}
                                            </span>
                                            <span className="font-medium text-gray-800">{keyLine(e.key)}</span>
                                            <span className="text-xs text-gray-500">
                                                {new Date(e.changedAt).toLocaleString('de-CH')} · {e.actor}
                                            </span>
                                        </div>

                                        {e.action === 'UPDATE' && e.changedFields.length > 0 && (
                                            <ul className="mt-1 ml-1 space-y-0.5 text-xs text-gray-700">
                                                {e.changedFields.map(name => (
                                                    <li key={name}>
                                                        <span className="text-gray-500">
                                                            {tOr(`config.field.${name}`, name)}:
                                                        </span>{' '}
                                                        <span className="line-through text-gray-500">
                                                            {show(e.before?.[name], emptyLabel, notSetLabel)}
                                                        </span>{' → '}
                                                        <span className="font-medium">
                                                            {show(e.after?.[name], emptyLabel, notSetLabel)}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {e.action === 'UPDATE' && e.changedFields.length === 0 && (
                                            <p className="mt-1 ml-1 text-xs text-gray-500">
                                                {t('config.historyNoFieldChange')}
                                            </p>
                                        )}
                                        {(e.action === 'INSERT' || e.action === 'DELETE') && (
                                            <p className="mt-1 ml-1 text-xs text-gray-600">
                                                {contract.fields
                                                    .filter(f => !f.key)
                                                    .map(f => `${tOr(`config.field.${f.name}`, f.name)}: `
                                                        + show((e.after ?? e.before)?.[f.name], emptyLabel, notSetLabel))
                                                    .join(' · ')}
                                            </p>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            {truncated && (
                                <p className="mt-2 text-xs text-gray-500">{t('config.historyTruncated')}</p>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
